import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const TestIntegrationPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [testResults, setTestResults] = useState([]);
  const navigate = useNavigate();

  // Fonction identique à celle intégrée dans CoupleContributionsPage
  const testCoupleAPI = async (identifier) => {
    try {
      setLoading(true);
      setError('');
      
      console.log('🔍 Testing couple contributions for:', identifier);
      
      // EXACTEMENT LA MÊME LOGIQUE QUE LA PAGE COUPLE CONTRIBUTIONS
      const apiUrl = `${process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000/api'}/dev/contributions/couple/${encodeURIComponent(identifier)}`;
      console.log('🌐 API URL:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });
      
      console.log('📡 Response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Error response:', errorText);
        throw new Error(`Erreur ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      console.log('📥 API Response:', data);
      
      // L'API retourne directement les données (couple_data, contributions, statistics)
      // Pas de wrapper {success: true, data: {...}}
      
      if (data && data.couple_data && data.contributions) {
        setData(data);
        
        // Ajouter le résultat du test
        setTestResults(prev => [...prev, {
          identifier,
          status: 'success',
          timestamp: new Date().toLocaleTimeString(),
          contributionsCount: data.contributions.length,
          statistics: data.statistics
        }]);
        
        return data;
      } else {
        throw new Error('Structure de données invalide');
      }
      
    } catch (error) {
      console.error('❌ Test failed for', identifier, ':', error);
      setError(`Test ${identifier}: ${error.message}`);
      
      // Ajouter le résultat d'erreur
      setTestResults(prev => [...prev, {
        identifier,
        status: 'error',
        timestamp: new Date().toLocaleTimeString(),
        error: error.message
      }]);
      
      return null;
    } finally {
      setLoading(false);
    }
  };

  const runAllTests = async () => {
    setTestResults([]);
    
    const tests = [
      'GOGO',
      '5', 
      'INEXISTANT'
    ];
    
    for (const test of tests) {
      await new Promise(resolve => setTimeout(resolve, 500)); // Délai entre les tests
      await testCoupleAPI(test);
    }
  };

  useEffect(() => {
    // Test automatique au montage
    testCoupleAPI('GOGO');
  }, []);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        {/* En-tête */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                🧪 Test d'Intégration - API Couple Contributions
              </h1>
              <p className="text-gray-600">
                Cette page teste la même logique API que CoupleContributionsPage.js
              </p>
            </div>
            <button
              onClick={() => navigate('/contributions')}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
            >
              ← Retour
            </button>
          </div>
        </div>

        {/* Contrôles de test */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <h2 className="text-xl font-semibold mb-4">🎮 Contrôles de Test</h2>
          <div className="flex gap-4 flex-wrap">
            <button
              onClick={() => testCoupleAPI('GOGO')}
              disabled={loading}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50"
            >
              🔴 Test GOGO
            </button>
            <button
              onClick={() => testCoupleAPI('5')}
              disabled={loading}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50"
            >
              🆔 Test ID 5
            </button>
            <button
              onClick={() => testCoupleAPI('INEXISTANT')}
              disabled={loading}
              className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
            >
              ❌ Test Inexistant
            </button>
            <button
              onClick={runAllTests}
              disabled={loading}
              className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:opacity-50"
            >
              🚀 Tous les tests
            </button>
          </div>
        </div>

        {/* État de chargement */}
        {loading && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
              <span className="text-blue-800">⏳ Test en cours...</span>
            </div>
          </div>
        )}

        {/* Erreur */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <span className="text-red-800">❌ {error}</span>
            </div>
          </div>
        )}

        {/* Résultats des tests */}
        {testResults.length > 0 && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">📊 Historique des Tests</h2>
            <div className="space-y-3">
              {testResults.map((result, index) => (
                <div key={index} className={`p-4 rounded-lg border-l-4 ${
                  result.status === 'success' 
                    ? 'bg-green-50 border-green-400' 
                    : 'bg-red-50 border-red-400'
                }`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <span className={`font-semibold ${
                        result.status === 'success' ? 'text-green-800' : 'text-red-800'
                      }`}>
                        {result.status === 'success' ? '✅' : '❌'} Test "{result.identifier}"
                      </span>
                      <span className="text-gray-500 ml-2">- {result.timestamp}</span>
                    </div>
                    {result.status === 'success' && (
                      <div className="text-sm text-green-600">
                        {result.contributionsCount} contributions trouvées
                      </div>
                    )}
                  </div>
                  {result.error && (
                    <div className="text-red-600 text-sm mt-1">{result.error}</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Données actuelles */}
        {data && (
          <div className="bg-white rounded-lg shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4">📋 Données du Dernier Test Réussi</h2>
            
            {/* Informations du couple */}
            <div className="bg-blue-50 rounded-lg p-4 mb-6">
              <h3 className="text-lg font-semibold text-blue-900 mb-3">ℹ️ Couple Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <span className="font-medium">Nom:</span> {data.couple_data?.couple_name || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Date de mariage:</span> {data.couple_data?.marriage_date || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Email:</span> {data.couple_data?.email || 'N/A'}
                </div>
                <div>
                  <span className="font-medium">Téléphone:</span> {data.couple_data?.phone || 'N/A'}
                </div>
              </div>
            </div>

            {/* Statistiques */}
            {data.statistics && (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
                  <h4 className="text-green-800 font-semibold">📊 Total</h4>
                  <p className="text-2xl font-bold text-green-900">{data.statistics.totalContributions || 0}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                  <h4 className="text-blue-800 font-semibold">💰 Payé</h4>
                  <p className="text-2xl font-bold text-blue-900">
                    {(data.statistics.totalPaid || 0).toLocaleString()} XOF
                  </p>
                </div>
                <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">
                  <h4 className="text-yellow-800 font-semibold">⏳ En attente</h4>
                  <p className="text-2xl font-bold text-yellow-900">
                    {(data.statistics.totalPending || 0).toLocaleString()} XOF
                  </p>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
                  <h4 className="text-purple-800 font-semibold">📈 Pourcentage</h4>
                  <p className="text-2xl font-bold text-purple-900">
                    {Math.round(data.statistics.paymentPercentage || 0)}%
                  </p>
                </div>
              </div>
            )}

            {/* Liste des contributions */}
            {data.contributions && data.contributions.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-4">📋 Contributions ({data.contributions.length})</h3>
                <div className="grid gap-4">
                  {data.contributions.slice(0, 5).map((contribution, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg border-l-4 border-blue-400">
                      <div className="flex justify-between items-start">
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            {contribution.event_name} - {contribution.period_year}
                          </h4>
                          <p className="text-gray-600">Type: {contribution.contribution_type}</p>
                          {contribution.payment_date && (
                            <p className="text-sm text-gray-500">
                              Payé le: {new Date(contribution.payment_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-gray-900">
                            {contribution.amount.toLocaleString()} XOF
                          </p>
                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                            contribution.status === 'paid'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {contribution.status === 'paid' ? '✅ Payé' : '⏳ En attente'}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {data.contributions.length > 5 && (
                    <div className="text-center text-gray-500 py-2">
                      ... et {data.contributions.length - 5} autres contributions
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Données brutes */}
            <details className="mt-6">
              <summary className="cursor-pointer font-semibold text-gray-700 hover:text-gray-900">
                📄 Voir les données brutes JSON
              </summary>
              <pre className="mt-2 bg-gray-100 p-4 rounded-lg overflow-x-auto text-sm">
                {JSON.stringify(data, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </div>
    </div>
  );
};

export default TestIntegrationPage;